import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mail,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  ShieldAlert,
  ArrowLeft,
  Clock
} from 'lucide-react';
import { Button } from '../../shared/components/ui';

/**
 * OTP Verification Form Props
 */
interface OtpVerificationFormProps {
  /** Username for verification */
  username: string;
  /** Masked email where OTP was sent */
  otpSentTo: string;
  /** Initial expiration time in seconds */
  initialExpiresIn: number;
  /** Callback to verify OTP */
  onVerify: (code: string) => Promise<{ success: boolean; error?: string; remainingAttempts?: number; isLocked?: boolean; lockRemainingSeconds?: number }>;
  /** Callback to resend OTP */
  onResend: () => Promise<{ success: boolean; expiresIn?: number; error?: string; isLocked?: boolean; lockRemainingSeconds?: number }>;
  /** Callback to go back to login form */
  onBack: () => void;
}

/**
 * OTP Verification Form Component
 * 
 * 6-digit OTP input with:
 * - Auto-focus between inputs
 * - Countdown timer
 * - Resend functionality
 * - Attempt tracking
 */
const OtpVerificationForm: React.FC<OtpVerificationFormProps> = ({
  username: _username,
  otpSentTo,
  initialExpiresIn,
  onVerify,
  onResend,
  onBack,
}) => {
  // OTP digits (6 individual inputs)
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(initialExpiresIn);
  const [canResend, setCanResend] = useState(false);
  const [lockCountdown, setLockCountdown] = useState(0);

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  // OTP lock countdown timer
  useEffect(() => {
    if (lockCountdown <= 0) return;

    const timer = setInterval(() => {
      setLockCountdown((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [lockCountdown]);

  const handleSubmit = useCallback(async () => {
    const code = digits.join('');
    if (code.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await onVerify(code);

      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || 'Invalid code');
        if (result.isLocked && result.lockRemainingSeconds) {
          setLockCountdown(result.lockRemainingSeconds);
        }
        if (result.remainingAttempts !== undefined) {
          setRemainingAttempts(result.remainingAttempts);
        }
        // Clear digits on error
        setDigits(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError((err as Error)?.message || 'Verification failed');
      setDigits(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  }, [digits, onVerify]);

  // Auto-submit when all digits entered
  useEffect(() => {
    const code = digits.join('');
    if (code.length === 6 && !isLoading && !success && lockCountdown <= 0) {
      void handleSubmit();
    }
  }, [digits, isLoading, success, lockCountdown, handleSubmit]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleDigitChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length > 0) {
      const newDigits = [...digits];
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pastedData[i] || '';
      }
      setDigits(newDigits);

      // Focus last filled or next empty
      const lastIndex = Math.min(pastedData.length, 5);
      inputRefs.current[lastIndex]?.focus();
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError(null);

    try {
      const result = await onResend();

      if (result.success) {
        // Reset countdown
        setCountdown(result.expiresIn || 180);
        setCanResend(false);
        setDigits(['', '', '', '', '', '']);
        setRemainingAttempts(null);
        inputRefs.current[0]?.focus();
      } else {
        setError(result.error || 'Failed to resend code');
        if (result.isLocked && result.lockRemainingSeconds) {
          setLockCountdown(result.lockRemainingSeconds);
        }
      }
    } catch (err) {
      setError((err as Error)?.message || 'Failed to resend code');
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const isOtpLocked = lockCountdown > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
      className="space-y-5"
    >
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500/10 rounded-full mb-4">
          <Mail className="w-8 h-8 text-primary-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">Verify Your Identity</h3>
        <p className="text-sm text-gray-500 mt-1">
          We've sent a 6-digit code to
        </p>
        <p className="text-sm font-medium text-gray-700">{otpSentTo}</p>
      </div>

      {/* Success State */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center py-8"
          >
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            <p className="text-green-600 font-medium">Verification successful!</p>
            <p className="text-sm text-gray-500 mt-1">Redirecting...</p>
          </motion.div>
        )}
      </AnimatePresence>

      {!success && (
        <>
          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-[#c00035] text-sm"
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Remaining Attempts Warning */}
          <AnimatePresence>
            {remainingAttempts !== null && remainingAttempts > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm"
              >
                <ShieldAlert className="w-4 h-4 flex-shrink-0" />
                <span>
                  {remainingAttempts === 1
                    ? 'Last attempt! Account will be locked after this.'
                    : `${remainingAttempts} attempts remaining`}
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* OTP Input Grid */}
          <div className="flex justify-center gap-2">
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleDigitChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isLoading || isOtpLocked}
                className={`
                  w-12 h-14 text-center text-2xl font-bold
                  bg-gray-50 border-2 rounded-xl
                  focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                  disabled:opacity-50 disabled:cursor-not-allowed
                  transition-all
                  ${error ? 'border-red-300 bg-red-50/50' : 'border-gray-200'}
                `}
              />
            ))}
          </div>

          {/* Countdown Timer */}
          <div className="text-center">
            {isOtpLocked ? (
              <div className="flex items-center justify-center gap-2 text-sm text-[#c00035]">
                <Clock className="w-4 h-4" />
                <span>
                  Try again in <span className="font-mono font-medium">{formatTime(lockCountdown)}</span>
                </span>
              </div>
            ) : countdown > 0 ? (
              <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                <Clock className="w-4 h-4" />
                <span>Code expires in <span className="font-mono font-medium text-primary-500">{formatTime(countdown)}</span></span>
              </div>
            ) : (
              <p className="text-sm text-amber-600">Code expired. You can request a new code.</p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={digits.join('').length !== 6 || isOtpLocked}
            isLoading={isLoading}
            variant="primary"
            size="lg"
            className="w-full"
          >
            {isLoading ? 'Verifying...' : 'Verify Code'}
          </Button>

          {/* Resend Code */}
          <div className="text-center space-y-2">
            <p className="text-sm text-gray-500">Didn't receive the code?</p>
            <Button
              type="button"
              onClick={handleResend}
              disabled={isOtpLocked || (!canResend && countdown > 0)}
              isLoading={isResending}
              variant="ghost"
              size="sm"
              icon={<RefreshCw className="w-4 h-4" />}
            >
              {isResending ? 'Sending...' : 'Resend Code'}
            </Button>
          </div>

          {/* Back Button */}
          <Button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            variant="ghost"
            size="sm"
            icon={<ArrowLeft className="w-4 h-4" />}
            className="w-full"
          >
            Back to login
          </Button>
        </>
      )}
    </motion.div>
  );
};

export default OtpVerificationForm;

